import React from "react";
import { Control, FieldErrors } from "react-hook-form";
import { FieldConfig, FieldType } from "models/formField";
import { TextInputField } from "components/dynamicFormElementsField/TextInputField";
import { TextAreaField } from "components/dynamicFormElementsField/TextAreaField";
import { CheckboxField } from "components/dynamicFormElementsField/CheckboxField";
import { RadioField } from "components/dynamicFormElementsField/RadioField";
import { SelectField } from "components/dynamicFormElementsField/SelectField";
import { DatePickerField } from "components/dynamicFormElementsField/DatePickerField";

/**
 * Get error message from nested field path
 * Handles both flat paths (e.g., "email") and nested paths (e.g., "personalData.userDob")
 * @param errors - FieldErrors object from react-hook-form
 * @param fieldPath - Field name/path (supports dot notation for nested fields)
 * @returns Error message string or undefined
 */
export const getFieldError = (
  errors: FieldErrors | undefined,
  fieldPath: string,
): string | undefined => {
  if (!errors || !fieldPath) return undefined;

  // Split the path by dots to handle nested paths
  const pathParts = fieldPath.split(".");
  let current: any = errors;

  // Traverse the nested object
  for (const part of pathParts) {
    if (current && typeof current === "object" && part in current) {
      current = current[part];
    } else {
      return undefined;
    }
  }

  // Return the message if it exists
  return current?.message as string | undefined;
};

/**
 * Props for FormField component
 */
export interface FormFieldProps {
  /** Field configuration */
  config: FieldConfig;

  /** React Hook Form control object - optional when using FormProvider */
  control?: Control<any>;

  /** Field errors from React Hook Form - optional when using FormProvider */
  errors?: FieldErrors;

  /** Watch function from React Hook Form - optional when using FormProvider */
  watch?: any;
}

/**
 * Get the field component based on field type
 */
export const getFieldComponent = (
  type: FieldType,
): React.ComponentType<any> | null => {
  switch (type) {
    case FieldType.TEXT:
    case FieldType.EMAIL:
    case FieldType.PASSWORD:
    case FieldType.NUMBER:
      return TextInputField;
    case FieldType.TEXTAREA:
      return TextAreaField;
    case FieldType.CHECKBOX:
      return CheckboxField;
    case FieldType.RADIO:
      return RadioField;
    case FieldType.SELECT:
      return SelectField;
    case FieldType.DATE:
      return DatePickerField;
    default:
      return null;
  }
};
